import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTaskStore } from '../../../store/useTaskStore';
import { useScheduleRuleStore } from '../../../store/useScheduleRuleStore';
import { useTaskChecklistStore } from '../../../store/useTaskChecklistStore';
import { useToastStore } from '../../../store/useToastStore';
import { showApiError } from '../../../errors';
import { Button } from '../../../components/Shared/Button';
import { Input } from '../../../components/Shared/Input';
import { FormField } from '../../../components/Shared/FormField';
import SearchSelect from '../../../components/Shared/SearchSelect';
import { MaterialManager } from './MaterialManager';
import { ScheduleRuleInlineEditor, type PendingScheduleRule } from './ScheduleRuleInlineEditor';
import { ChecklistEditor, type PendingChecklistItem } from './ChecklistEditor';
import type { TaskDto, TaskListDto, MaterialItem, CreateTaskRequest, UpdateTaskRequest } from '../../../types';
import type { FacilityFlowCoreModulesCustomersDtosObjectDto, FacilityFlowCoreModulesServicesDtosServiceDto } from '../../../api/generated/models/index';

/** Drops the client-only id a pending rule/checklist item carries before it is sent. */
function withoutLocalId<T extends { _localId: string }>(item: T): Omit<T, '_localId'> {
  const payload = { ...item } as Partial<T>;
  delete payload._localId;
  return payload as Omit<T, '_localId'>;
}

/** Start values for a new task, e.g. taken over from a report ("Meldung"). Ignored when editing. */
export interface TaskFormPrefill {
  objectId?: string;
  name?: string;
  description?: string;
}

interface TaskFormProps {
  task?: TaskListDto;
  onClose: () => void;
  objects: FacilityFlowCoreModulesCustomersDtosObjectDto[];
  services: FacilityFlowCoreModulesServicesDtosServiceDto[];
  prefill?: TaskFormPrefill;
  /** Called with the new task after a successful create, before the form closes. */
  onCreated?: (task: TaskDto) => void | Promise<void>;
}

export function TaskForm({ task, onClose, objects, services, prefill, onCreated }: TaskFormProps) {
  const { t } = useTranslation(['tasks', 'common']);
  const { createTask, updateTask, fetchTaskById, currentTask, loading } = useTaskStore();
  const { createRule } = useScheduleRuleStore();
  const { createItem: createChecklistItem } = useTaskChecklistStore();
  const { addToast } = useToastStore();

  const [pendingRules, setPendingRules] = useState<PendingScheduleRule[]>([]);
  const [pendingChecklist, setPendingChecklist] = useState<PendingChecklistItem[]>([]);

  const [serviceId, setServiceId] = useState(task?.serviceId || '');
  const [objectId, setObjectId] = useState(task?.objectId || prefill?.objectId || '');

  // Prefill object when creating a task and the form is scoped to a single object
  useEffect(() => {
    if (task) return;
    if (objectId) return;
    if (objects.length === 1 && objects[0]?.id) {
      setObjectId(objects[0].id);
    }
  }, [task, objectId, objects]);

  const [name, setName] = useState(task ? '' : (prefill?.name ?? ''));
  const [description, setDescription] = useState(task ? '' : (prefill?.description ?? ''));
  const [estimatedDurationMinutes, setEstimatedDurationMinutes] = useState(task?.estimatedDurationMinutes || 60);
  const [materials, setMaterials] = useState<MaterialItem[]>([]);
  const [instructions, setInstructions] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isBlocking, setIsBlocking] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load full task details for editing (only when taskId changes, not on every render)
  const editTaskId = task?.taskId;
  useEffect(() => {
    if (editTaskId) {
      fetchTaskById(editTaskId);
    }
  }, [editTaskId, fetchTaskById]);

  // Populate form when currentTask is loaded. why: only for the task being edited — the store
  // keeps the last loaded task, which must not leak into (or overwrite the prefill of) a new one.
  useEffect(() => {
    if (editTaskId && currentTask?.taskId === editTaskId) {
      setName(currentTask.name || '');
      setDescription(currentTask.description || '');
      setEstimatedDurationMinutes(currentTask.estimatedDurationMinutes);
      setMaterials(currentTask.materials || []);
      setInstructions(currentTask.instructions || '');
      setIsActive(currentTask.isActive);
      setIsBlocking(currentTask.isBlocking);
    }
  }, [currentTask, editTaskId]);

  // Auto-generate name preview
  const namePreview = name || (serviceId && objectId ? 
    `${services.find(s => s.id === serviceId)?.name} @ ${objects.find(o => o.id === objectId)?.name}` : 
    '');

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!serviceId) newErrors.serviceId = t('tasks:form.validation.serviceRequired');
    if (!objectId) newErrors.objectId = t('tasks:form.validation.objectRequired');
    if (estimatedDurationMinutes < 1) newErrors.estimatedDurationMinutes = t('tasks:form.validation.durationMin');
    if (estimatedDurationMinutes > 1440) newErrors.estimatedDurationMinutes = t('tasks:form.validation.durationMax');
    if (name && name.length > 200) newErrors.name = t('tasks:form.validation.nameMaxLength');
    if (description && description.length > 2000) newErrors.description = t('tasks:form.validation.descriptionMaxLength');
    if (instructions && instructions.length > 5000) newErrors.instructions = t('tasks:form.validation.instructionsMaxLength');

    // Validate materials
    materials.forEach((mat, index) => {
      if (!mat.name || mat.name.trim() === '') {
        newErrors[`material_${index}`] = t('tasks:form.validation.materialNameRequired', { index: index + 1 });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      addToast(t('tasks:messages.fixFormErrors'), 'error');
      return;
    }

    try {
      if (task) {
        // Update existing task (schedule rules are managed inline via ScheduleRuleInlineEditor)
        const updateData: UpdateTaskRequest = {
          name: name || undefined,
          description: description || undefined,
          estimatedDurationMinutes,
          materials: materials.length > 0 ? materials : undefined,
          instructions: instructions || undefined,
          isActive,
          isBlocking,
        };
        await updateTask(task.taskId, updateData);
        addToast(t('tasks:messages.updated'), 'success');
      } else {
        // Create new task, then attach pending schedule rules
        const createData: CreateTaskRequest = {
          serviceId,
          objectId,
          name: name || undefined,
          description: description || undefined,
          estimatedDurationMinutes,
          materials: materials.length > 0 ? materials : undefined,
          instructions: instructions || undefined,
          isBlocking,
        };
        const newTask = await createTask(createData);

        // Chain: create schedule rules with the new taskId
        if (pendingRules.length > 0) {
          const ruleResults = await Promise.allSettled(
            pendingRules.map((rule) => createRule(newTask.taskId, withoutLocalId(rule))),
          );
          const failedCount = ruleResults.filter((r) => r.status === 'rejected').length;
          if (failedCount > 0) {
            addToast(
              t('tasks:messages.createdWithRuleErrors', { failedCount, totalCount: pendingRules.length }),
              'warning',
            );
          } else {
            addToast(
              t('tasks:messages.createdWithRules', { count: pendingRules.length }),
              'success',
            );
          }
        } else {
          addToast(t('tasks:messages.created'), 'success');
        }

        // Chain: attach the checklist. why: sequential, not Promise.allSettled — SortOrder is
        // assigned server-side by append, so parallel writes would scramble the order the
        // user just arranged.
        if (pendingChecklist.length > 0) {
          let failedChecklistCount = 0;
          for (const item of pendingChecklist) {
            try {
              await createChecklistItem(newTask.taskId, withoutLocalId(item));
            } catch {
              failedChecklistCount++;
            }
          }
          if (failedChecklistCount > 0) {
            addToast(
              t('tasks:checklist.messages.createdWithErrors', {
                failedCount: failedChecklistCount,
                totalCount: pendingChecklist.length,
              }),
              'warning',
            );
          }
        }

        await onCreated?.(newTask);
      }
      onClose();
    } catch (err) {
      // why: the backend's error code says what is wrong — e.g. planning.tasks.alreadyExists
      // when the object already has a task for this service (FAFD-27). The form shows no
      // field errors itself, so the toast must not be skipped for them.
      showApiError(err, { skipIfFieldErrors: false });
    }
  };

  const formatDurationDisplay = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return t('tasks:duration.minutes', { count: mins });
    if (mins === 0) return t('tasks:duration.hours', { count: hours });
    return t('tasks:duration.hoursMinutes', { hours, minutes: mins });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Service Selection */}
      <FormField label={t('tasks:form.fields.service')} required error={errors.serviceId}>
        <SearchSelect
          options={services
            .map((service) => ({ id: service.id ?? '', label: service.name ?? '' }))
            .filter((o) => o.id)}
          value={serviceId}
          onChange={setServiceId}
          disabled={!!task}
          placeholder={t('tasks:placeholders.selectService')}
          noResultsText={t('tasks:filters.noResults')}
        />
      </FormField>

      {/* Object Selection */}
      <FormField label={t('tasks:form.fields.object')} required error={errors.objectId}>
        <SearchSelect
          options={objects
            .map((obj) => ({ id: obj.id ?? '', label: obj.name ?? '' }))
            .filter((o) => o.id)}
          value={objectId}
          onChange={setObjectId}
          disabled={!!task}
          placeholder={t('tasks:placeholders.selectObject')}
          noResultsText={t('tasks:filters.noResults')}
        />
      </FormField>

      {task && (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {t('tasks:form.labels.immutableServiceObject')}
        </div>
      )}

      {/* Name (optional) */}
      <FormField 
        label={t('tasks:form.fields.name')} 
        error={errors.name}
      >
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('tasks:placeholders.nameOptional')}
        />
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('tasks:form.labels.nameAutoGenerated')}
        </div>
        {namePreview && (
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t('tasks:form.labels.namePreview')} <span className="font-medium">{namePreview}</span>
          </div>
        )}
      </FormField>

      {/* Description */}
      <FormField label={t('tasks:form.fields.description')} error={errors.description}>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder={t('tasks:placeholders.descriptionOptional')}
        />
      </FormField>

      {/* Estimated Duration */}
      <FormField label={t('tasks:form.fields.estimatedDuration')} required error={errors.estimatedDurationMinutes}>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={estimatedDurationMinutes}
            onChange={(e) => setEstimatedDurationMinutes(parseInt(e.target.value) || 0)}
            min={1}
            max={1440}
            className="w-32"
          />
          <span className="text-gray-600 dark:text-gray-400">{t('tasks:duration.minutesUnit')}</span>
          <span className="text-sm text-gray-500">
            ({formatDurationDisplay(estimatedDurationMinutes)})
          </span>
        </div>
      </FormField>

      {/* Schedule Rules */}
      <ScheduleRuleInlineEditor
        taskId={task?.taskId}
        pendingRules={!task ? pendingRules : undefined}
        onPendingRulesChange={!task ? setPendingRules : undefined}
      />

      {/* Checklist */}
      <FormField label={t('tasks:checklist.title')}>
        <ChecklistEditor
          taskId={task?.taskId}
          pendingItems={!task ? pendingChecklist : undefined}
          onPendingItemsChange={!task ? setPendingChecklist : undefined}
        />
      </FormField>

      {/* Materials */}
      <MaterialManager
        materials={materials}
        onChange={setMaterials}
        errors={errors}
      />

      {/* Instructions */}
      <FormField label={t('tasks:form.fields.instructions')} error={errors.instructions}>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={5}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
          placeholder={t('tasks:placeholders.instructionsPlaceholder')}
        />
      </FormField>

      <FormField label={t('tasks:form.fields.blocking')}>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isBlocking}
            onChange={(e) => setIsBlocking(e.target.checked)}
            className="mt-0.5 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-[13px] text-slate-700 dark:text-slate-200">
            {t('tasks:form.fields.isBlocking')}
          </span>
        </label>
      </FormField>

      {/* Active Status (only for edit) */}
      {task && (
        <FormField label={t('tasks:form.fields.status')}>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-gray-900 dark:text-white">{t('tasks:form.fields.isActive')}</span>
          </label>
        </FormField>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onClose}>
          {t('tasks:form.actions.cancel')}
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? t('tasks:form.actions.saving') : task ? t('tasks:form.actions.update') : t('tasks:form.actions.create')}
        </Button>
      </div>
    </form>
  );
}
